import React from "react";

import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElement } from "@/components/ui-kit/TextElement/TextElement";
import { ContentfulProduct, SearchImageType } from "@/types/common";
import { cn } from "@/utils/general";

import styles from "./ResultCard.module.scss";

/**
 * ResultCard displays an image and text for a search result or item.
 */
interface ResultCardProps {
  data: ContentfulProduct;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>, item: ContentfulProduct) => void;
  isSearchResult?: boolean;
}

function getImageUrl(searchImage: SearchImageType): string | undefined {
  if (typeof searchImage === "string") {
    return searchImage; // direct URL string
  }
  if ("file" in searchImage) {
    return searchImage.file.url;
  }
  // It's probably a Link<"Asset"> or unknown, so no URL here
  return undefined;
}

const ResultCard: React.FC<ResultCardProps> = ({ data, isSearchResult = false }) => {
  // Safely construct image URL or fallback to empty string
  const imageUrl = getImageUrl(data?.searchImage)
    ? `https://${getImageUrl(data?.searchImage)}?fm=jpg&fl=progressive`
    : undefined;

  return (
    <div
      className={cn(
        "rounded-md bg-master-base-secondary   md:w-full",
        styles.searchItemImageContainer
      )}
      data-is-search-result={isSearchResult ? "true" : "false"}
    >
      {imageUrl ? (
        <Picture
          width={236}
          height={178}
          src={imageUrl}
          alt={data.title || ""}
          className="rounded-t-md w-full object-cover aspect-[47/36]"
        />
      ) : null}
      <div className={cn("flex flex-col items-center p-2", styles.searchImageText)}>
        <TextElement
          className="body--regular-b text-master-primary-secondary text-center"
          text={data.title}
        />
        <TextElement
          className="body--small text-master-base-black text-center"
          text={data.subTitle}
        />
      </div>
    </div>
  );
};

export default ResultCard;
